import React, { useState, useRef, useEffect } from 'react';
import './MaskedInput.scss';

export interface MaskedInputProps {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
  mask: string; // Example: "AAAAAA" where A = any alphanumeric, or "AAA-AAAA-AAA" for dashed format
  convertCase?: 'upper' | 'lower' | 'none';
  groupPattern?: 'numbers-letters' | 'none'; // Group numbers with numbers, letters with letters
}

/**
 * MaskedInput component with automatic formatting
 * Supports:
 * - Visual masking with separators (hyphens)
 * - Automatic case conversion
 * - Character grouping (optional)
 * - Max length enforcement
 */
const MaskedInput: React.FC<MaskedInputProps> = ({
  value,
  onChange,
  onKeyDown,
  placeholder = '',
  className = '',
  autoFocus = false,
  mask,
  convertCase = 'upper',
  groupPattern = 'none'
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [cursorPosition, setCursorPosition] = useState<number | null>(null);

  // Parse mask to get separator positions and max length
  const getMaskInfo = () => {
    const separators: number[] = [];
    let charCount = 0;
    
    for (let i = 0; i < mask.length; i++) {
      if (mask[i] === '-') {
        separators.push(charCount);
      } else {
        charCount++;
      }
    }
    
    return { separators, maxLength: charCount };
  };

  const { separators, maxLength } = getMaskInfo();

  // Apply case conversion
  const applyCase = (text: string): string => {
    if (convertCase === 'upper') return text.toUpperCase();
    if (convertCase === 'lower') return text.toLowerCase();
    return text;
  };

  // Format value with separators
  const formatValue = (rawValue: string): string => {
    // Remove all non-alphanumeric characters
    const cleaned = rawValue.replace(/[^a-zA-Z0-9]/g, '');
    const converted = applyCase(cleaned);
    
    // If no separators in mask, return the cleaned value directly
    if (separators.length === 0) {
      return converted.slice(0, maxLength);
    }
    
    // Build formatted string with separators
    let formatted = '';
    let charIndex = 0;
    
    for (let i = 0; i < converted.length && charIndex < maxLength; i++) {
      // Check if we need to add a separator
      if (separators.includes(charIndex)) {
        formatted += '-';
      }
      formatted += converted[i];
      charIndex++;
    }
    
    return formatted;
  };

  // Get raw value without separators
  const getRawValue = (formattedValue: string): string => {
    return formattedValue.replace(/-/g, '');
  };

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const raw = getRawValue(inputValue);
    
    // Limit to max length
    if (raw.length > maxLength) {
      return;
    }
    
    // Store cursor position before formatting
    const selectionStart = e.target.selectionStart || 0;
    
    // Apply formatting
    const formatted = formatValue(raw);
    onChange(getRawValue(formatted));
    
    // Calculate new cursor position
    if (separators.length === 0) {
      // For masks without separators, cursor position is straightforward
      setCursorPosition(Math.min(selectionStart, formatted.length));
    } else {
      // Count how many characters are before cursor (excluding separators)
      let charsBeforeCursor = 0;
      for (let i = 0; i < selectionStart && i < inputValue.length; i++) {
        if (inputValue[i] !== '-') {
          charsBeforeCursor++;
        }
      }
      
      // Find the position in formatted string
      let newPosition = 0;
      let charCount = 0;
      for (let i = 0; i < formatted.length; i++) {
        if (formatted[i] !== '-') {
          charCount++;
        }
        if (charCount >= charsBeforeCursor) {
          newPosition = i + 1;
          break;
        }
      }
      
      // Adjust for separator if we're right after one
      if (newPosition < formatted.length && formatted[newPosition] === '-') {
        newPosition++;
      }
      
      setCursorPosition(newPosition);
    }
  };

  // Update cursor position after render
  useEffect(() => {
    if (cursorPosition !== null && inputRef.current) {
      inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
      setCursorPosition(null);
    }
  }, [cursorPosition]);

  // Generate placeholder with mask pattern
  const generatePlaceholder = (): string => {
    if (placeholder) return placeholder;
    
    let result = '';
    let charIndex = 0;
    
    for (let i = 0; i < mask.length; i++) {
      if (mask[i] === '-') {
        result += '-';
      } else {
        result += '_';
        charIndex++;
      }
    }
    
    return result;
  };

  const displayValue = formatValue(value);

  return (
    <div className={`masked-input ${className}`}>
      <input
        ref={inputRef}
        type="text"
        value={displayValue}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        placeholder={generatePlaceholder()}
        className="masked-input__field"
        autoFocus={autoFocus}
        maxLength={mask.length}
      />
      <div className="masked-input__hint">
        {getRawValue(displayValue).length} / {maxLength}
      </div>
    </div>
  );
};

export default MaskedInput;

